import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowRight, Star, MapPin, Shield, Award, CheckCircle, Sparkles, Clock, FileSearch, Users, Camera, Hammer, ShieldCheck, BookOpen } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import { useState, useEffect, useRef, lazy, Suspense } from "react";

import ServiceCard from "@/components/shared/ServiceCard";
import TestimonialCard from "@/components/shared/TestimonialCard";
import CallbackMicroForm from "@/components/shared/CallbackMicroForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { COMPANY, SERVICES, SERVICE_AREAS, TESTIMONIALS, FAQ_ITEMS } from "@/lib/constants";
import { generateFaqSchema } from "@/components/seo/SchemaGenerator";
import SafeFloridaHomeCTA from "@/components/shared/SafeFloridaHomeCTA";
import FinancingCTA from "@/components/shared/FinancingCTA";
import SeasonalUrgencyBanner from "@/components/shared/SeasonalUrgencyBanner";
import { useVisitorTracking } from "@/hooks/useVisitorTracking";

// Phase 4: Lazy load ProjectCarousel to defer network request
const ProjectCarousel = lazy(() => import("@/components/shared/ProjectCarousel"));

// Carousel placeholder for loading state
const CarouselPlaceholder = () => (
  <div className="w-full h-64 bg-muted rounded-xl animate-pulse flex items-center justify-center">
    <span className="text-muted-foreground text-sm">Loading projects...</span>
  </div>
);

// Homepage @graph schema - authoritative business entity
const homepageGraphSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": "https://obriencontractingusa.com/#website",
      "url": "https://obriencontractingusa.com/",
      "name": "O'Brien Contracting",
      "inLanguage": "en-US",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://obriencontractingusa.com/?s={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@type": "Organization",
      "@id": "https://obriencontractingusa.com/#organization",
      "name": "O'Brien Contracting",
      "url": "https://obriencontractingusa.com/",
      "telephone": "+1-239-919-4485",
      "areaServed": {
        "@type": "State",
        "name": "Florida"
      },
      "sameAs": [
        "https://www.facebook.com/profile.php?id=61563400679102",
        "https://www.instagram.com/obriencontractingusa/",
        "https://www.yelp.com/biz/o-brien-contracting-north-port",
        "https://www.linkedin.com/company/o-brien-contracting"
      ]
    },
    {
      "@type": "LocalBusiness",
      "@id": "https://obriencontractingusa.com/#localbusiness",
      "name": "O'Brien Contracting",
      "legalName": "O'Brien Contracting, LLC",
      "url": "https://obriencontractingusa.com/",
      "telephone": "+1-239-919-4485",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "4063 Fonsica Ave",
        "addressLocality": "North Port",
        "addressRegion": "FL",
        "postalCode": "34286",
        "addressCountry": "US"
      },
      "founder": "Christopher O'Brien",
      "identifier": "CCC1335947",
      "priceRange": "$$",
      "areaServed": [
        { "@type": "City", "name": "North Port" },
        { "@type": "City", "name": "Port Charlotte" },
        { "@type": "City", "name": "Venice" },
        { "@type": "City", "name": "Englewood" },
        { "@type": "City", "name": "Punta Gorda" },
        { "@type": "City", "name": "Sarasota" },
        { "@type": "City", "name": "Bradenton" }
      ]
    },
    {
      "@type": "WebPage",
      "@id": "https://obriencontractingusa.com/#webpage",
      "url": "https://obriencontractingusa.com/",
      "name": "North Port Roofing Contractor | O'Brien Contracting",
      "isPartOf": { "@id": "https://obriencontractingusa.com/#website" },
      "about": { "@id": "https://obriencontractingusa.com/#localbusiness" },
      "primaryImageOfPage": {
        "@type": "ImageObject",
        "@id": "https://obriencontractingusa.com/#primaryimage"
      }
    }
  ]
};

// Review schema for rich snippets
const reviewsSchema = {
  "@context": "https://schema.org",
  "@graph": TESTIMONIALS.slice(0, 5).map((t, i) => ({
    "@type": "Review",
    "@id": `https://obriencontractingusa.com/#review-${i + 1}`,
    itemReviewed: {
      "@type": "RoofingContractor",
      "@id": "https://obriencontractingusa.com/#localbusiness",
      name: COMPANY.name
    },
    author: { "@type": "Person", name: t.name },
    datePublished: t.datePublished,
    reviewRating: {
      "@type": "Rating",
      ratingValue: t.rating,
      bestRating: 5,
      worstRating: 1
    },
    reviewBody: t.text
  }))
};

// FAQ schema for rich snippets
const faqSchema = generateFaqSchema(FAQ_ITEMS);

const Index = () => {
  const { trackCTAClick } = useVisitorTracking();

  // Show hero-preload on homepage only (it was hidden in main.tsx)
  useEffect(() => {
    const heroPreload = document.getElementById("hero-preload");
    if (heroPreload) {
      heroPreload.style.display = "";
    }
    return () => {
      if (heroPreload) {
        heroPreload.style.display = "none";
      }
    };
  }, []);

  // Phase 4: Intersection observer for lazy loading ProjectCarousel
  const [showCarousel, setShowCarousel] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowCarousel(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px', threshold: 0 }
    );
    
    if (carouselRef.current) {
      observer.observe(carouselRef.current);
    }
    
    return () => observer.disconnect();
  }, []);
  
  return (
    <PublicLayout
      title="South Florida Roofing Contractor | O'Brien Contracting"
      description="Residential and commercial roofing throughout South Florida — North Port, Sarasota, Venice, Boca Raton, Delray Beach and Palm Beach County. Tile, metal, shingle and commercial roofs. License CCC1335947."
      includeBusinessSchemas={true}
      breadcrumbs={[
        { name: "Home", url: "https://obriencontractingusa.com/" }
      ]}
    >
      {/* Homepage @graph Schema + Review Schema + FAQ Schema */}
      <Helmet>
        <link rel="alternate" hrefLang="es" href="https://obriencontractingusa.com/es" />
        <link rel="alternate" hrefLang="en" href="https://obriencontractingusa.com/" />
        <script type="application/ld+json">
          {JSON.stringify(homepageGraphSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      {/* Seasonal Urgency Banner */}
      <SeasonalUrgencyBanner />

      {/* Hero Section - uses pre-loaded image from index.html for instant LCP */}
      <section className="relative min-h-hero flex items-center bg-hero-florida">

        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 border border-accent/30 mb-6">
              <Star className="w-4 h-4 text-accent fill-accent" />
              <span className="text-sm font-semibold text-primary-foreground">
                {COMPANY.googleRating} Stars on Google • {COMPANY.googleReviews}+ Reviews
              </span>
            </div>

            {/* Heading */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-primary-foreground leading-tight mb-6">
              <span className="text-accent">South Florida's</span> Residential &amp; Commercial Roofing Specialists
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-primary-foreground/95 mb-8 max-w-2xl">
              O'Brien Contracting provides professional residential and commercial roofing
              services throughout South Florida, including North Port, Sarasota, Venice, Boca
              Raton, Delray Beach, Palm Beach County, and surrounding communities. License CCC1335947.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <Link to="/quick-estimate" className="w-full sm:w-auto">
                <Button 
                  variant="outline" 
                  size="xl"
                  className="w-full sm:w-auto h-auto min-h-12 py-3 whitespace-normal text-center bg-white text-primary border-white hover:bg-white/90 shadow-[0_0_20px_rgba(255,255,255,0.3)] font-bold"
                  data-track-cta="hero_get_estimate"
                  onClick={() => trackCTAClick('Get Free Roofing Estimate', 'hero')}
                >
                  Get Free Roofing Estimate
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/roof-visualizer">
                <Button 
                  variant="accent" 
                  size="xl"
                  className="shadow-[0_0_20px_rgba(196,167,94,0.4)] text-white"
                  data-track-cta="hero_visualizer"
                  onClick={() => trackCTAClick('AI Roof Visualizer', 'hero')}
                >
                  <Sparkles className="mr-2 h-5 w-5" />
                  Visualize Your Roof
                </Button>
              </Link>
            </div>

            {/* Callback Micro-Form */}
            <div className="mt-6">
              <p className="text-primary-foreground/95 text-sm mb-2">Or request a callback:</p>
              <CallbackMicroForm />
            </div>
            <div className="flex flex-wrap gap-6 mt-12 pt-8 border-t border-primary-foreground/20">
              <div className="flex items-center gap-2 text-primary-foreground">
                <Shield className="w-5 h-5 text-accent" />
                <span className="text-sm font-medium">Licensed CCC1335947</span>
              </div>
              <div className="flex items-center gap-2 text-primary-foreground">
                <Award className="w-5 h-5 text-accent" />
                <span className="text-sm font-medium">{COMPANY.yearsExperience}+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2 text-primary-foreground">
                <Clock className="w-5 h-5 text-accent" />
                <span className="text-sm font-medium">24/7 Emergency Available</span>
              </div>
              <div className="flex items-center gap-2 text-primary-foreground">
                <FileSearch className="w-5 h-5 text-accent" />
                <span className="text-sm font-medium">Free Inspections</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* My Safe Florida Home Grant CTA */}
      <SafeFloridaHomeCTA />

      {/* Financing CTA — Prominent */}
      <section className="py-10 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <FinancingCTA variant="prominent" />
        </div>
      </section>


      {/* Services Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-accent font-semibold text-sm uppercase tracking-wider">
              Our Services
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mt-2 mb-4">
              Complete Roofing Solutions
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              From repairs to full replacements, we handle all your roofing needs 
              with expert craftsmanship and quality materials.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {SERVICES.slice(0, 8).map((service) => (
              <ServiceCard key={service.slug} {...service} />
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/services">
              <Button 
                variant="outline" 
                size="lg"
                data-track-cta="services_view_all"
                onClick={() => trackCTAClick('View All Services', 'services_section')}
              >
                View All Services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Service Areas Section - Phase 7: content-visibility optimization */}
      <section className="py-20 bg-secondary content-auto">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-accent font-semibold text-sm uppercase tracking-wider">
              Service Areas
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mt-2 mb-4">
              Serving North Port & the Gulf Coast Corridor
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Headquartered in North Port, we serve homeowners and businesses in North Port,
              Venice, Englewood, Port Charlotte, Sarasota, and Bradenton.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICE_AREAS.map((area) => (
              <Link
                key={area.slug}
                to={`/roofing/${area.slug}`}
                className="group bg-card rounded-2xl p-5 shadow-soft hover:shadow-card transition-all duration-300 hover:-translate-y-1 border border-border hover:border-accent/30"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 group-hover:bg-accent/20 transition-colors">
                    <MapPin className="w-5 h-5 text-accent" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-heading font-bold text-foreground mb-1 group-hover:text-accent transition-colors">
                      {area.name}
                    </h3>
                    <p className="text-muted-foreground text-sm">{area.description}</p>
                    <div className="flex items-center gap-1 text-accent font-semibold text-xs mt-2">
                      View Services
                      <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/roof-replacement-sarasota-fl" className="text-sm font-semibold text-accent hover:underline">
              Roof Replacement in Sarasota, FL
            </Link>
            <span className="text-muted-foreground/50">•</span>
            <Link to="/roof-replacement-venice-fl" className="text-sm font-semibold text-accent hover:underline">
              Roof Replacement in Venice, FL
            </Link>
            <span className="text-muted-foreground/50">•</span>
            <Link to="/roofing-contractor-north-port-fl" className="text-sm font-semibold text-accent hover:underline">
              Roofing Contractor in North Port, FL
            </Link>
            <span className="text-muted-foreground/50">•</span>
            <Link to="/metal-roofing-naples-fl" className="text-sm font-semibold text-accent hover:underline">
              Metal Roofing in Naples, FL
            </Link>
          </div>
        </div>
      </section>


      {/* Roofing Guides & Resources */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-accent font-semibold text-sm uppercase tracking-wider">Expert Resources</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mt-2">Roofing Guides & Resources</h2>
            <p className="text-muted-foreground text-lg mt-3 max-w-2xl mx-auto">In-depth guides to help you make informed roofing decisions</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { path: '/guides/roof-replacement-cost', title: 'Roof Replacement Cost Guide', desc: 'Florida-specific pricing by material, financing options & when to repair vs replace.' },
              { path: '/guides/insurance-claim-process', title: 'Insurance Claim Process', desc: 'Step-by-step walkthrough for filing a roof insurance claim in Florida.' },
              { path: '/guides/wind-mitigation', title: 'Wind Mitigation Guide', desc: 'How to save 15–45% on insurance with a wind mitigation inspection.' },
              { path: '/guides/solar-roofing', title: 'Solar Roofing Guide', desc: 'Prepare your roof for solar panels — incentives, materials & energy savings.' },
            ].map((guide) => (
              <Link key={guide.path} to={guide.path} className="group bg-card rounded-2xl p-6 shadow-soft hover:shadow-card transition-all border border-border hover:border-accent/30 hover:-translate-y-1">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
                  <BookOpen className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-heading font-bold text-foreground mb-2 group-hover:text-accent transition-colors">{guide.title}</h3>
                <p className="text-muted-foreground text-sm mb-3">{guide.desc}</p>
                <span className="text-accent font-semibold text-sm flex items-center gap-1">
                  Read Guide <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section - Phase 7: content-visibility optimization */}
      <section className="py-20 bg-background content-auto">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-accent font-semibold text-sm uppercase tracking-wider">
                About O'Brien Contracting
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mt-2 mb-6">
                Family-Owned Excellence Since 2016
              </h2>
              <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
                For over {COMPANY.yearsExperience} years, O'Brien Contracting has been
                North Port's trusted roofing partner — serving Sarasota, Charlotte, Manatee
                and Lee counties. We combine old-fashioned craftsmanship with modern
                techniques to deliver roofing solutions that stand the test of time—and
                Florida weather.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Licensed & Insured (CCC1335947)",
                  "Lifetime Workmanship Guarantee",
                  "Price Value Guarantee",
                  "Financing Options Available",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/about">
                <Button 
                  variant="default" 
                  size="lg"
                  data-track-cta="about_learn_more"
                  onClick={() => trackCTAClick('Learn More About Us', 'about_section')}
                >
                  Learn More About Us
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="min-w-0 w-full overflow-hidden">
              {/* Phase 4: Lazy load ProjectCarousel when in viewport */}
              <div ref={carouselRef} className="min-w-0 w-full">
                {showCarousel ? (
                  <Suspense fallback={<CarouselPlaceholder />}>
                    <ProjectCarousel />
                  </Suspense>
                ) : (
                  <CarouselPlaceholder />
                )}
              </div>
              <div className="text-center mt-6">
                <Link to="/projects">
                  <Button variant="outline" size="sm">
                    View All Projects
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose O'Brien Section */}
      <section className="py-20 bg-background content-auto">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-accent font-semibold text-sm uppercase tracking-wider">
              The O'Brien Difference
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mt-2 mb-4">
              Why Choose O'Brien Over Other Roofers?
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              We do things differently — and our customers notice.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: ShieldCheck, title: "No High-Pressure Sales", desc: "Honest assessments, transparent pricing, and zero pressure. We let our work speak for itself." },
              { icon: FileSearch, title: "Full Insurance Claim Assistance", desc: "We handle the paperwork, meet with adjusters, and fight for fair compensation on your behalf." },
              { icon: Shield, title: "Licensed & Insured (CCC1335947)", desc: "Fully licensed State Certified Roofing Contractor with comprehensive liability and workers' comp coverage." },
              { icon: Users, title: "In-House Crews Only", desc: "No subcontractors. Our trained crews handle every project for consistent quality and accountability." },
              { icon: Camera, title: "Detailed Photo Documentation", desc: "Complete before, during, and after photo documentation of every project for your records and insurance." },
              { icon: Hammer, title: "Clean Job Site Guarantee", desc: "We treat your property with respect — magnetic nail sweeps, daily cleanup, and a spotless finish." },
            ].map((item) => (
              <div key={item.title} className="bg-card rounded-2xl p-6 shadow-soft border border-border hover:border-accent/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-accent" />
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Phase 7: content-visibility optimization */}
      <section className="py-20 bg-secondary content-auto">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-accent font-semibold text-sm uppercase tracking-wider">
              Testimonials
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mt-2 mb-4">
              What Our Customers Say
            </h2>
            <div className="flex items-center justify-center gap-2 mb-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-accent text-accent" />
              ))}
              <span className="ml-2 text-lg font-semibold text-foreground">
                {COMPANY.googleRating} out of 5
              </span>
            </div>
            <p className="text-muted-foreground">
              Based on {COMPANY.googleReviews}+ Google reviews
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.slice(0, 3).map((testimonial, i) => (
              <TestimonialCard key={i} {...testimonial} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section - Phase 7: content-visibility optimization */}
      <section className="py-20 bg-background content-auto">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-accent font-semibold text-sm uppercase tracking-wider">
                Common Questions
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mt-2 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-muted-foreground text-lg">
                Answers to the most common roofing questions from North Port and Gulf Coast homeowners.
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {FAQ_ITEMS.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-card rounded-xl border border-border px-6 shadow-soft"
                >
                  <AccordionTrigger className="text-left text-foreground hover:text-accent hover:no-underline py-5 font-semibold">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="text-center mt-10">
              <p className="text-muted-foreground mb-4">
                Have more questions? We're here to help!
              </p>
              <Link to="/contact">
                <Button 
                  variant="default"
                  data-track-cta="faq_contact"
                  onClick={() => trackCTAClick('Contact Us', 'faq_section')}
                >
                  Contact Us
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Location Map Section */}
      <section className="py-20 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-accent font-semibold text-sm uppercase tracking-wider">
              Find Us
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mt-2 mb-4">
              Two Primary Florida Markets
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              O'Brien Contracting provides residential and commercial roofing throughout Southwest Florida and South Florida's East Coast, including North Port, Sarasota, Venice, Boca Raton, Delray Beach, and Palm Beach County. We are a service-area contractor — we come to you.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3 text-sm font-medium">
              <a href="/roofing/north-port" className="rounded-md border border-border bg-background px-4 py-2 hover:border-accent">North Port</a>
              <a href="/roofing/sarasota" className="rounded-md border border-border bg-background px-4 py-2 hover:border-accent">Sarasota</a>
              <a href="/roofing/palm-beach-county" className="rounded-md border border-border bg-background px-4 py-2 hover:border-accent">Palm Beach County</a>
              <a href="/roofing/boca-raton" className="rounded-md border border-border bg-background px-4 py-2 hover:border-accent">Boca Raton</a>
              <a href="/roofing/delray-beach" className="rounded-md border border-border bg-background px-4 py-2 hover:border-accent">Delray Beach</a>
            </div>
          </div>
          <div className="max-w-4xl mx-auto rounded-xl overflow-hidden border border-border shadow-soft">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3552.357397476569!2d-82.19878642388791!3d27.082024652792697!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x64f03ccfde61d69b%3A0x69628c245f428a0!2sO'Brien%20Contracting!5e0!3m2!1sen!2sus!4v1786071196305!5m2!1sen!2sus"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="O'Brien Contracting Map - North Port, FL"
            />
          </div>
        </div>
      </section>

    </PublicLayout>
  );
};

export default Index;
